import React from "react";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { Search, X } from "lucide-react";
import HeaderSearch from "./header-search";

// Columns that hold ids rather than text - matching on them would only add noise.
const SKIP_KEY = /(^id$|Id$|_id$|guid$|Guid$)/;

// Filters a page's rows by their visible text: any text or number in the row, so a
// search matches the same things the user can read in the table.
//
// getSearchText overrides that guess with exactly what the page displays per
// row. Pass it whenever the raw row differs from what's on screen - hidden
// fields (messages, raw ISO timestamps, counts) otherwise match terms the
// user can't see, and displayed text that isn't stored as-is (formatted
// dates, a status label derived from a boolean, a zero-padded id) can't be
// found at all.
export function useListSearch<T extends Record<string, any>>(rows: T[], getSearchText?: (row: T) => Array<string | number | null | undefined>) {
    const [term, setTerm] = React.useState('');
    const filtered = React.useMemo(() => {
        const needle = term.trim().toLowerCase();
        if (!needle) return rows;
        const matches = (value: unknown) => value !== null && value !== undefined
            && (typeof value === 'string' || typeof value === 'number')
            && String(value).toLowerCase().includes(needle);
        if (getSearchText) return rows.filter(row => getSearchText(row).some(matches));
        return rows.filter(row => Object.keys(row).some(key => !SKIP_KEY.test(key) && matches(row[key])));
    }, [rows, term, getSearchText]);
    return { term, setTerm, filtered, total: rows.length, shown: filtered.length, searching: term.trim() !== '' };
}

type ListSearchState = ReturnType<typeof useListSearch>;

// Page title with the list's search box and item count beside it, like the Invoices header.
// On a phone there's no room for all three beside the menu button and user menu -
// it wrapped into a stacked mess - so it shows the title, count and a search icon;
// the icon swaps the title for a full-width search box (open while a search is applied).
export const ListTitle: React.FC<{ title: string; list: ListSearchState; placeholder?: string }> = ({ title, list, placeholder = "Search..." }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchOpen, setSearchOpen] = React.useState(false);
    const count = list.total != null && (
        <Box
            component="span"
            title={list.searching ? `${list.shown} of ${list.total} match` : undefined}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: 16,
                fontWeight: 600,
                background: 'var(--color-container-background)',
                color: 'var(--color-text-muted)',
                flexShrink: 0,
            }}
        >
            {list.searching ? list.shown : list.total}
        </Box>
    );

    if (isMobile) {
        if (searchOpen || list.searching) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <HeaderSearch value={list.term} onSearch={list.setTerm} placeholder={placeholder} width="100%" />
                    </Box>
                    <IconButton size="small" aria-label="Close search" onClick={() => { list.setTerm(''); setSearchOpen(false); }}>
                        <X size={18} />
                    </IconButton>
                </Box>
            );
        }
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</Box>
                {count}
                <IconButton size="small" aria-label="Search" onClick={() => setSearchOpen(true)} sx={{ color: 'var(--color-primary)' }}>
                    <Search size={20} />
                </IconButton>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {title}
            <HeaderSearch value={list.term} onSearch={list.setTerm} placeholder={placeholder} width={200} />
            {count}
        </Box>
    );
};
